/**
 * Question item-quality (discrimination) color coding, ported 1:1 from the
 * legacy QuestionManager. In the legacy app the colored 10px left border on
 * each question card is **not** based on the editorial `QuestionQuality` lookup
 * — it is computed from the item's **point-biserial discrimination index** and
 * bucketed into Poor / Acceptable / Good / Excellent.
 *
 * Verified against legacy source:
 *  - `Core/ItemAnalysis/ItemQualityRules.cs` → `CalculateItemQuality(disIndex)`
 *  - `Usercontrols/QuestionDataList.ascx.cs` → `var disindex = vm.PointBiSerial ?? 0`
 *    then the `excellent/good/acceptable/poor` CSS class is set from the result.
 *  - Colors: `sass/_qmQuestionList.scss`. The filter UI labels this
 *    "Discrimination" (internally `Value="Quality"`).
 *
 * Thresholds (on point-biserial):
 *   null or 0      → none (neutral grey, no color)
 *   ≤ 0.19         → Poor        `#dc8888`
 *   0.20 – 0.29    → Acceptable  `#effe5a`
 *   0.30 – 0.49    → Good        `#d5d987`
 *   ≥ 0.50         → Excellent   `#7ab971`
 */

/** A discrimination bucket. Ordered worst → best. */
export type DiscriminationBucket = 'poor' | 'acceptable' | 'good' | 'excellent';

/** Verified legacy hexes (`_qmQuestionList.scss` lines 180-198). */
export const DISCRIMINATION_COLORS: Record<DiscriminationBucket, string> = {
  poor: '#dc8888',
  acceptable: '#effe5a',
  good: '#d5d987',
  excellent: '#7ab971',
};

/** Neutral border for items with no discrimination data (never delivered, or pb = 0). */
export const DISCRIMINATION_NEUTRAL = '#cccccc';

/** Human-readable label for each bucket (matches the legacy `LabelStandard` text). */
export const DISCRIMINATION_LABELS: Record<DiscriminationBucket, string> = {
  poor: 'Poor',
  acceptable: 'Acceptable',
  good: 'Good',
  excellent: 'Excellent',
};

/**
 * Resolve the discrimination bucket for a point-biserial value, using the exact
 * legacy `ItemQualityRules` thresholds. Returns `null` for missing/zero values
 * (legacy `ItemQuality.None` — no color).
 */
export function discriminationBucket(
  pointBiserial: number | null | undefined,
): DiscriminationBucket | null {
  if (pointBiserial == null || pointBiserial === 0) return null;
  if (pointBiserial <= 0.19) return 'poor';
  if (pointBiserial <= 0.29) return 'acceptable';
  if (pointBiserial <= 0.49) return 'good';
  return 'excellent'; // ≥ 0.50
}

/**
 * Resolve the quality-border color for a point-biserial value. Returns the
 * neutral grey when there is no discrimination data.
 */
export function discriminationColor(pointBiserial: number | null | undefined): string {
  const bucket = discriminationBucket(pointBiserial);
  return bucket ? DISCRIMINATION_COLORS[bucket] : DISCRIMINATION_NEUTRAL;
}

/**
 * Shared legacy tokens reused across the redesign. Defined once here and
 * surfaced on the theme (`palette.legacy`) so screens never inline these hexes.
 */
export const LEGACY_TOKENS = {
  /** Used-in-exam folder icon, active (green). */
  usedInExam: '#7ab971',
  /** Inactive icon / neutral quality border. */
  inactive: DISCRIMINATION_NEUTRAL,
  /** Card / row hover background. */
  hover: '#f2f2f2',
  /** Exam-taking: answered question indicator (orange). */
  examAnswered: '#F49100',
  /** Exam-taking: answered indicator border. */
  examAnsweredBorder: '#475B7E',
  /** Dark header background (top nav + exam header). */
  header: '#3d495f',
  /** Dark header muted text. */
  headerText: '#aaaaaa',
  /** Exam-taking: beige page body. */
  examBody: '#e9e8e3',
  /** Exam-taking: centered white column border. */
  examColumnBorder: '#666666',
  /** Active horizontal nav item background. */
  navActive: '#666666',
  /** Light divider between sections. */
  divider: '#cccccc',
} as const;

/**
 * The legacy token surface as a themeable type: same keys, but each value
 * widened to `string` so alternate brand themes (e.g. the v2 marketing palette)
 * can supply different hexes for the same slots.
 */
export type LegacyTokens = { [K in keyof typeof LEGACY_TOKENS]: string };
